import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import PostMenu from "../components/PostMenu";
import DownloadButton from "../components/DownloadButton";
import LikeButton from "../components/LikeButton";
import SaveButton from "../components/SaveButton";
import Comments from "../components/Comments";
import HashtagInput from "../components/HashtagInput";
import ImageLightbox from "../components/ImageLightbox";
import TruncatedText from "../components/TruncatedText";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import LazyImage from "../components/LazyImage";
import PostSkeleton from "../components/PostSkeleton";
import Toast from "../components/Toast";
import { extractHashtags, buildLegacyCaption } from "../utils/hashtags";
import { createNotification } from "../utils/notifications";
import { likeDesign } from "../utils/likes";
import ReportModal from "../components/ReportModal";
import VerifiedBadge from "../components/VerifiedBadge";
import TagInfoModal from "../components/TagInfoModal";
import { openPinterestPin } from "../utils/pinterest";
import { getBlockedUserIds } from "../utils/blocks";
import { getMutedUserIds } from "../utils/mutes";
import { readFeedCache, writeFeedCache } from "../utils/feedCache";
import { fetchMyEngagement, attachMyEngagementBindings } from "../utils/myEngagement";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import { removeDesignStorage } from "../utils/designStorage";
import { useLanguage } from "../context/LanguageContext";
import { confirmDialog, confirmLogin } from "../utils/dialog";
import {
  IoEllipsisVertical,
  IoCreateOutline,
  IoTrashOutline,
  IoCheckmark,
  IoClose,
  IoPricetag,
  IoLogoPinterest,
  IoFlag,
  IoPeople,
  IoCompass,
} from "react-icons/io5";
import { PiChatCircleBold } from "react-icons/pi";

const PAGE_SIZE = 10;
// How many of your own + followed posts one request fetches; more come in as you scroll.
const FOLLOWING_FETCH_SIZE = 50;
const TITLE_LIMIT = 500; // matches designs.title's limit in Upload.jsx
// Only what the feed actually renders or edits (see the post card below, and
// startEdit -> buildLegacyCaption, which needs `hashtags`) — "select *" was
// also pulling columns like `score` that nothing here reads. Whether the
// current viewer has liked/saved each one is fetched separately (see
// utils/myEngagement.js) — that's no longer stored on the design row itself.
const FEED_COLUMNS =
  "id, user_id, created_at, title, description, hashtags, image_url, images, design_url, design_files, likes, downloads, comment_count, users(username, avatar_url, verified)";

function Home() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // The last feed seen on this device (same account), drawn at once while the fresh one is
  // fetched — so a slow connection shows posts, not grey placeholders.
  const [cachedFeed] = useState(() => readFeedCache(getStoredUser()?.id ?? null));

  const [designs, setDesigns] = useState(() => cachedFeed?.designs || []); // combined: following + suggested, tagged with _section
  const [followingIds, setFollowingIds] = useState(() => cachedFeed?.followingIds || []);
  const [blockedIds, setBlockedIds] = useState(() => cachedFeed?.blockedIds || []);
  const [mutedIds, setMutedIds] = useState(() => cachedFeed?.mutedIds || []);

  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState("");
  const [editToast, setEditToast] = useState("");

  const [user, setUser] = useState(getStoredUser);

  // Instagram-style UI state
  const [openMenuId, setOpenMenuId] = useState(null); // which card's ⋮ menu is open
  const [tagModalId, setTagModalId] = useState(null); // which card's tag-info popup is open
  const [reportModalId, setReportModalId] = useState(null); // which card's report modal is open
  // Defaults to Explore for someone who (as far as the cached feed knows)
  // doesn't follow anyone yet — a brand-new account has nothing to show on
  // an empty Following tab, so discovery is the more useful first screen.
  const [activeTab, setActiveTab] = useState(() =>
    (cachedFeed?.followingIds?.length > 0 ? "following" : "explore")
  );
  const [openComments, setOpenComments] = useState({}); // { [designId]: true/false }
  const [lightboxImage, setLightboxImage] = useState(null);
  const menuRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  // Pagination for the "Suggested for you" section, loaded PAGE_SIZE at a
  // time (like Instagram) instead of all at once — keeps initial load and
  // Supabase read usage small.
  const [suggestedOffset, setSuggestedOffset] = useState(cachedFeed ? PAGE_SIZE : 0);
  const [hasMoreSuggested, setHasMoreSuggested] = useState(cachedFeed ? cachedFeed.hasMoreSuggested : true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(!!cachedFeed);
  // True once the fresh feed has arrived (or failed). Until then "load more" waits: the
  // fresh first page is about to replace what is on screen, and would drop pages added first.
  const [feedFresh, setFeedFresh] = useState(false);
  // Shown when the very first load (nothing saved to show yet) is taking long.
  const [slowLoad, setSlowLoad] = useState(false);
  // Posts of you + the people you follow are fetched FOLLOWING_FETCH_SIZE at a time; only this
  // many are drawn, and it grows by PAGE_SIZE as you scroll, like the suggested ones.
  const [followingShown, setFollowingShown] = useState(PAGE_SIZE);
  // Server-side paging for the Following tab: how many rows were fetched so far, and whether
  // the last fetch came back full (so there may be more). Stays false until the fresh feed arrives.
  const [followingOffset, setFollowingOffset] = useState(0);
  const [hasMoreFollowing, setHasMoreFollowing] = useState(false);

  useEffect(() => {
    if (initialLoaded) return undefined;
    const timer = setTimeout(() => setSlowLoad(true), 12000);
    return () => clearTimeout(timer);
  }, [initialLoaded]);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      setUser(session?.user ?? null);
    }

    getUser();
  }, []);

  useEffect(() => {
    async function fetchDesigns() {
      const {
        data: { session },
        error: userError
      } = await supabase.auth.getSession();

      const currentUser = session?.user ?? null;

      if (userError) {
        console.log("USER ERROR:", userError);
      }

      let myFollowingIds = [];
      let myBlockedIds = [];
      let myMutedIds = [];

      if (currentUser) {
        // Capped defensively — this list feeds into IN/NOT-IN filters below,
        // so an account following an extreme number of people can't blow up
        // those query strings on every visit.
        const [{ data: followsData, error: followsError }, blockedIds, mutedIds] = await Promise.all([
          supabase
            .from("follows")
            .select("following_id")
            .eq("follower_id", currentUser.id)
            .limit(500),
          getBlockedUserIds(currentUser.id),
          getMutedUserIds(currentUser.id)
        ]);

        if (followsError) {
          console.log("FOLLOWS ERROR:", followsError);
        }

        myFollowingIds = (followsData || []).map(f => f.following_id);
        myBlockedIds = blockedIds;
        myMutedIds = mutedIds;
        setFollowingIds(myFollowingIds);
        setBlockedIds(myBlockedIds);
        setMutedIds(myMutedIds);
      }

      const excludeIds = currentUser
        ? [currentUser.id, ...myFollowingIds, ...myBlockedIds, ...myMutedIds]
        : [];

      let suggestedQuery = supabase
        .from("designs")
        .select(FEED_COLUMNS)
        .order("created_at", { ascending: false })
        .range(0, PAGE_SIZE - 1);

      if (excludeIds.length > 0) {
        suggestedQuery = suggestedQuery.not(
          "user_id",
          "in",
          `(${excludeIds.join(",")})`
        );
      }

      // ===== FOLLOWING FEED + SUGGESTED/DISCOVER FEED — independent of each
      // other (both only depend on myFollowingIds above), so run them in
      // parallel instead of one after another. The suggested feed is paged
      // PAGE_SIZE at a time (below, via loadMoreSuggested); the following
      // feed is fetched FOLLOWING_FETCH_SIZE rows at a time (more via
      // loadMoreFollowing) so a heavy-follow account can't pull an
      // unbounded number of rows on every visit. =====
      const [followingRes, suggestedRes] = await Promise.all([
        currentUser
          ? supabase
              .from("designs")
              .select(FEED_COLUMNS)
              .in("user_id", [currentUser.id, ...myFollowingIds])
              .order("created_at", { ascending: false })
              .range(0, FOLLOWING_FETCH_SIZE - 1)
          : Promise.resolve({ data: [], error: null }),
        suggestedQuery,
      ]);

      if (followingRes.error) {
        console.log("FOLLOWING DESIGNS ERROR:", followingRes.error);
      }
      if (suggestedRes.error) {
        console.log("SUGGESTED DESIGNS ERROR:", suggestedRes.error);
      }

      const followingDesigns = (followingRes.data || [])
        .filter(d => !myBlockedIds.includes(d.user_id) && !myMutedIds.includes(d.user_id))
        .map(d => ({ ...d, _section: "following" }));
      const suggestedDesigns = (suggestedRes.data || []).map(d => ({
        ...d,
        _section: "suggested"
      }));

      let mergedFollowing = followingDesigns;
      let mergedSuggested = suggestedDesigns;

      if (currentUser) {
        const { likedIds, savedIds } = await fetchMyEngagement(
          [...followingDesigns, ...suggestedDesigns].map(d => d.id),
          currentUser.id
        );
        const withEngagement = d => ({
          ...d,
          _likedByMe: likedIds.has(d.id),
          _savedByMe: savedIds.has(d.id)
        });
        mergedFollowing = followingDesigns.map(withEngagement);
        mergedSuggested = suggestedDesigns.map(withEngagement);
      }

      const combined = [...mergedFollowing, ...mergedSuggested];

      // comment_count lives directly on designs (a trigger-maintained
      // column, same idea as likes/downloads) — no separate query needed.
      const hasMore = (suggestedRes.data || []).length === PAGE_SIZE;

      setDesigns(combined);
      setSuggestedOffset(PAGE_SIZE);
      setHasMoreSuggested(hasMore);
      // Counted on the raw rows (before the blocked/muted filter), since that is what the range pages over.
      setFollowingOffset(FOLLOWING_FETCH_SIZE);
      setHasMoreFollowing((followingRes.data || []).length === FOLLOWING_FETCH_SIZE);
      setInitialLoaded(true);
      setFeedFresh(true);

      // Only the first screenful is kept for the next visit.
      writeFeedCache(currentUser?.id ?? null, {
        designs: [...mergedFollowing.slice(0, PAGE_SIZE), ...mergedSuggested],
        followingIds: myFollowingIds,
        blockedIds: myBlockedIds,
        mutedIds: myMutedIds,
        hasMoreSuggested: hasMore
      });
    }

    // An error anywhere in the load (a session check that fails, a dropped
    // connection) used to leave the loading placeholders up for good.
    fetchDesigns().catch(err => {
      console.log("LOAD FEED ERROR:", err);
      setInitialLoaded(true);
      setFeedFresh(true);
    });
  }, []);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* liked/saved each one is a separate, per-user concern — see the
  // myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);

  // Live updates for this viewer's own likes/saves (another tab/device on
  // the same account) plus follows/unfollows from anywhere — merged onto
  // one channel instead of two, since both are keyed to this same user and
  // share the same mount/unmount lifecycle (see utils/myEngagement.js).
  useEffect(() => {
    if (!user) return undefined;

    const setEngagement = (designId, fields) => {
      setDesigns(prev => prev.map(d => (d.id === designId ? { ...d, ...fields } : d)));
    };

    const channel = attachMyEngagementBindings(supabase.channel(`home-live-${user.id}`), user.id, {
      onLike: id => setEngagement(id, { _likedByMe: true }),
      onUnlike: id => setEngagement(id, { _likedByMe: false }),
      onSave: id => setEngagement(id, { _savedByMe: true }),
      onUnsave: id => setEngagement(id, { _savedByMe: false })
    });

    channel
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "follows", filter: `follower_id=eq.${user.id}` },
        payload => {
          const followedId = payload.new.following_id;
          setFollowingIds(prev => (prev.includes(followedId) ? prev : [...prev, followedId]));
          setDesigns(prev =>
            prev.map(d => (d.user_id === followedId ? { ...d, _section: "following" } : d))
          );
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "follows", filter: `follower_id=eq.${user.id}` },
        payload => {
          const unfollowedId = payload.old.following_id;
          setFollowingIds(prev => prev.filter(id => id !== unfollowedId));
          setDesigns(prev =>
            prev.map(d => (d.user_id === unfollowedId ? { ...d, _section: "suggested" } : d))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Close ⋮ menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔒 Gate any action that needs login (like, comment, download, follow)
  function requireLogin(action) {
    if (user) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  async function handleDelete(id) {
    const ok = await confirmDialog({
      message: "Delete this design?",
      confirmText: t("common.delete"),
      danger: true
    });
    if (!ok) return;

    // Read from local state before the row is gone — it already has this design's
    // photo/file references, so no extra fetch is needed to know what to clean up.
    const design = designs.find(d => d.id === id);

    const { error } = await supabase.from("designs").delete().eq("id", id);

    if (error) {
      console.log(error);
      return;
    }

    setDesigns(prev => prev.filter(d => d.id !== id));
    setOpenMenuId(null);

    // Deleting the row doesn't remove the actual files from Storage — best-effort,
    // and not awaited, so a slow Storage response never delays the UI update above.
    if (design) {
      removeDesignStorage(design).catch(err => console.log("DESIGN STORAGE CLEANUP ERROR:", err));
    }
  }

  function startEdit(design) {
    setEditingId(design.id);
    setEditCaption(buildLegacyCaption(design));
    setOpenMenuId(null);
  }

  async function saveEdit(id) {
    // The textarea's maxLength only blocks further TYPING past the limit —
    // it doesn't retroactively trim a value set programmatically, and
    // startEdit populates this from buildLegacyCaption, which can produce
    // a longer string than TITLE_LIMIT for an old post whose separate
    // title+description together once exceeded it. Without this, saving
    // straight after opening the editor (no typing at all) could silently
    // persist a title past the limit every other post enforces.
    const title = editCaption.trim().slice(0, TITLE_LIMIT);
    const hashtags = extractHashtags(title);

    // startEdit builds editCaption from buildLegacyCaption, which folds a
    // pre-merge post's separate `description` into the caption text being
    // edited here — but saving only ever wrote title/hashtags back, leaving
    // the old `description` column untouched. The card renders title AND
    // (separately) description when present, so every legacy post ended up
    // showing its own description twice after being edited once. Clearing
    // it here completes the one-time upgrade buildLegacyCaption started.
    const { error } = await supabase
      .from("designs")
      .update({ title, hashtags, description: null })
      .eq("id", id);

    if (error) {
      console.log(error);
      setEditToast("Couldn't save. Try again.");
      return;
    }

    setDesigns(prev =>
      prev.map(d => (d.id === id ? { ...d, title, hashtags, description: null } : d))
    );
    setEditingId(null);
  }

  // 🔄 Re-fetch the live comment count for one design (used when closing the panel)
  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d =>
        d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d
      )
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];

    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));

    if (isCurrentlyOpen) {
      refreshCommentCount(id);
    }
  }

  // ➕ Follow a user directly from a suggested card
  async function handleFollow(targetUserId) {
    if (!user) return;

    const { error } = await supabase
      .from("follows")
      .insert({ follower_id: user.id, following_id: targetUserId });

    if (error) {
      console.log("FOLLOW ERROR:", error);
      return;
    }

    createNotification({ userId: targetUserId, actorId: user.id, type: "follow" });

    setFollowingIds(prev => [...prev, targetUserId]);
    setDesigns(prev =>
      prev.map(d => (d.user_id === targetUserId ? { ...d, _section: "following" } : d))
    );
  }

  // Load the next PAGE_SIZE suggested designs (infinite scroll), keeping
  // the same "not already following, not yourself" filter as the first page.
  async function loadMoreSuggested() {
    if (!feedFresh || loadingMoreRef.current || !hasMoreSuggested) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const excludeIds = user ? [user.id, ...followingIds, ...blockedIds, ...mutedIds] : [];

    let query = supabase
      .from("designs")
      .select(FEED_COLUMNS)
      .order("created_at", { ascending: false })
      .range(suggestedOffset, suggestedOffset + PAGE_SIZE - 1);

    if (excludeIds.length > 0) {
      query = query.not("user_id", "in", `(${excludeIds.join(",")})`);
    }

    const { data, error } = await query;

    if (error) {
      console.log("LOAD MORE SUGGESTED ERROR:", error);
      loadingMoreRef.current = false;
      setLoadingMore(false);
      return;
    }

    let newDesigns = (data || []).map(d => ({ ...d, _section: "suggested" }));

    if (user) {
      const { likedIds, savedIds } = await fetchMyEngagement(newDesigns.map(d => d.id), user.id);
      newDesigns = newDesigns.map(d => ({
        ...d,
        _likedByMe: likedIds.has(d.id),
        _savedByMe: savedIds.has(d.id)
      }));
    }

    setDesigns(prev => [...prev, ...newDesigns]);
    setSuggestedOffset(prev => prev + PAGE_SIZE);
    setHasMoreSuggested(newDesigns.length === PAGE_SIZE);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  // Load the next FOLLOWING_FETCH_SIZE posts of you + the people you follow, once every
  // post fetched so far is on screen. Same blocked/muted filter and like/save marks as the first page.
  async function loadMoreFollowing() {
    if (!feedFresh || !user || loadingMoreRef.current || !hasMoreFollowing) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const { data, error } = await supabase
        .from("designs")
        .select(FEED_COLUMNS)
        .in("user_id", [user.id, ...followingIds])
        .order("created_at", { ascending: false })
        .range(followingOffset, followingOffset + FOLLOWING_FETCH_SIZE - 1);

      if (error) {
        console.log("LOAD MORE FOLLOWING ERROR:", error);
        // Stop asking: the scroll watcher would otherwise re-fire this request on every
        // failure. A reload brings the paging back.
        setHasMoreFollowing(false);
        return;
      }

      let newDesigns = (data || [])
        .filter(d => !blockedIds.includes(d.user_id) && !mutedIds.includes(d.user_id))
        .map(d => ({ ...d, _section: "following" }));

      const { likedIds, savedIds } = await fetchMyEngagement(newDesigns.map(d => d.id), user.id);
      newDesigns = newDesigns.map(d => ({
        ...d,
        _likedByMe: likedIds.has(d.id),
        _savedByMe: savedIds.has(d.id)
      }));

      // A post can already be on screen (e.g. it moved into Following when you followed
      // its author from Explore), so skip ids that are there already.
      setDesigns(prev => {
        const have = new Set(prev.map(d => d.id));
        return [...prev, ...newDesigns.filter(d => !have.has(d.id))];
      });
      setFollowingOffset(prev => prev + FOLLOWING_FETCH_SIZE);
      setHasMoreFollowing((data || []).length === FOLLOWING_FETCH_SIZE);
    } catch (err) {
      console.log("LOAD MORE FOLLOWING ERROR:", err);
      setHasMoreFollowing(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  const followingList = designs.filter(d => d._section === "following");
  const suggestedList = designs.filter(d => d._section === "suggested");
  const moreFollowingHidden = followingList.length > followingShown;

  // Infinite scroll: which tab is active decides what the sentinel loads
  // more of — Following first draws more of the already-fetched list, then
  // fetches the next batch from the server; Explore fetches the next page of
  // suggested designs from the server.
  const sentinelActive =
    activeTab === "following" ? moreFollowingHidden || hasMoreFollowing : hasMoreSuggested;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !feedFresh || !sentinelActive) return;

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0].isIntersecting) return;
        if (activeTab === "following") {
          if (moreFollowingHidden) setFollowingShown(n => n + PAGE_SIZE);
          else loadMoreFollowing();
        } else loadMoreSuggested();
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedFresh, suggestedOffset, hasMoreSuggested, loadingMore, user, followingIds, blockedIds, mutedIds, followingShown, moreFollowingHidden, hasMoreFollowing, followingOffset, activeTab]);

  // click-intercept helper for guarded buttons
  function guardClick(e) {
    if (!user) {
      e.preventDefault();
      e.stopPropagation();
      confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
        if (goLogin) navigate("/login");
      });
    }
  }

  // ===== Card renderer (shared by both sections) =====
  function renderCard(design) {
    const isOwner = user && user.id === design.user_id;
    const commentsOpen = !!openComments[design.id];
    const commentCount = design.comment_count || 0;

    function updateDesign(fields) {
      setDesigns(prev =>
        prev.map(d => (d.id === design.id ? { ...d, ...fields } : d))
      );
    }

    const isFollowingAuthor = followingIds.includes(design.user_id);
    const showFollowBtn =
      design._section === "suggested" && user && !isOwner && !isFollowingAuthor;

    return (
      <div key={design.id} style={card}>
        {/* ===== HEADER: avatar + username + follow + ⋮ menu ===== */}
        <div style={cardHeader}>
          <Link
            to={design.user_id === user?.id ? "/profile" : `/u/${design.user_id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flex: 1,
              minWidth: 0,
              textDecoration: "none",
              color: "var(--text-primary)"
            }}
          >
            <LazyImage
              src={design.users?.avatar_url || "/default-avatar.svg"}
              alt={`${design.users?.username || "User"}'s avatar`}
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid var(--border-medium)",
                flexShrink: 0
              }}
            />
            <span style={postNameColumn}>
              <span style={{ fontWeight: 700, fontSize: "18px" }}>
                @{design.users?.username || "unknown"}
                {design.users?.verified && <VerifiedBadge />}
              </span>
              <PostTime date={design.created_at} />
            </span>
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {showFollowBtn && (
              <button
                onClick={() => requireLogin(() => handleFollow(design.user_id))}
                style={followBtn}
              >
                {t("common.follow")}
              </button>
            )}

            {design._section === "suggested" && !user && (
              <button
                onClick={() => requireLogin(() => {})}
                style={followBtn}
              >
                {t("common.follow")}
              </button>
            )}

            <div
              style={{ position: "relative" }}
              ref={openMenuId === design.id ? menuRef : null}
            >
              <button
                onClick={() =>
                  setOpenMenuId(openMenuId === design.id ? null : design.id)
                }
                style={dotsBtn}
                aria-label="More options"
              >
                <IoEllipsisVertical />
              </button>

              {openMenuId === design.id && (
                <PostMenu
                  items={[
                    { icon: <IoPricetag />, label: "Tag info", onClick: () => { setOpenMenuId(null); setTagModalId(design.id); } },
                    { icon: <IoLogoPinterest color="#E60023" />, label: "Pin on Pinterest", onClick: () => { setOpenMenuId(null); openPinterestPin(design); } },
                    ...(isOwner
                      ? [
                          { icon: <IoCreateOutline />, label: "Edit", onClick: () => startEdit(design) },
                          { icon: <IoTrashOutline />, label: "Delete", danger: true, onClick: () => handleDelete(design.id) }
                        ]
                      : user?.id
                      ? [
                          { icon: <IoFlag />, label: "Report", onClick: () => { setOpenMenuId(null); setReportModalId(design.id); } }
                        ]
                      : [])
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        {/* ===== IMAGE(S) ===== */}
        <ImageCarousel
          images={design.images?.length > 0 ? design.images : [design.image_url]}
          alt={design.title}
          imgStyle={{ ...image, cursor: "zoom-in" }}
          onImageClick={setLightboxImage}
          onDoubleTap={() =>
            likeDesign({ designId: design.id, user, designOwnerId: design.user_id, onUpdate: updateDesign, likeOnly: true })
          }
        />

        {/* ===== ACTION ROW (login required) ===== */}
        <div style={actionRow}>
          <span onClickCapture={e => guardClick(e)} style={{ display: "contents" }}>
            <LikeButton
              designId={design.id}
              likes={design.likes}
              liked={design._likedByMe}
              user={user}
              designOwnerId={design.user_id}
              onUpdate={updateDesign}
            />
          </span>

          <button
            onClick={() => requireLogin(() => toggleComments(design.id))}
            style={commentBadgeBtn}
            aria-label="Toggle comments"
          >
            <PiChatCircleBold size={27} /> <span style={commentCountText}>{commentCount}</span>
          </button>

          <span onClickCapture={e => guardClick(e)} style={{ display: "contents" }}>
            <DownloadButton
              fileURL={design.design_url}
              files={design.design_files}
              designId={design.id}
              downloads={design.downloads}
              designOwnerId={design.user_id}
              user={user}
              onUpdate={updateDesign}
            />
          </span>

          <ShareButton designId={design.id} />

          <span
            onClickCapture={e => guardClick(e)}
            style={{ display: "inline-flex", marginLeft: "auto" }}
          >
            <SaveButton
              designId={design.id}
              saved={design._savedByMe}
              user={user}
              onUpdate={updateDesign}
            />
          </span>
        </div>

        {/* ===== CAPTION / EDIT FORM ===== */}
        <div style={editingId === design.id ? { ...content, paddingBottom: 14 } : content}>
          {editingId === design.id ? (
            <>
              <HashtagInput
                multiline
                rows={4}
                value={editCaption}
                onChange={setEditCaption}
                maxLength={TITLE_LIMIT}
                placeholder="Write a caption… #phulkari #wedding #punjabi"
              />

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => saveEdit(design.id)} style={saveBtn}>
                  <IoCheckmark size={16} /> Save
                </button>
                <button onClick={() => setEditingId(null)} style={cancelBtn}>
                  <IoClose size={16} /> Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: "0 0 4px", whiteSpace: "pre-wrap" }}>
                <Link
                  to={design.user_id === user?.id ? "/profile" : `/u/${design.user_id}`}
                  style={{ fontWeight: 700, fontSize: 16, color: "var(--text-primary)", textDecoration: "none" }}
                >
                  @{design.users?.username || "unknown"}
                </Link>{" "}
                <TruncatedText text={design.title} />
              </p>
              {design.description && (
                <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
                  {design.description}
                </p>
              )}
            </>
          )}

          {commentsOpen && (
            <Comments designId={design.id} onClose={() => toggleComments(design.id)} designOwnerId={design.user_id} />
          )}
        </div>
      </div>
    );

  }

  const activeList = activeTab === "following" ? followingList.slice(0, followingShown) : suggestedList;

  return (
    <div style={page}>
      <FeaturedStrip excludeIds={[...blockedIds, ...mutedIds]} />

      <div style={tabRow} role="group" aria-label="Feed">
        <button
          style={activeTab === "following" ? tabActive : tabBtn}
          onClick={() => setActiveTab("following")}
          aria-pressed={activeTab === "following"}
        >
          <IoPeople size={15} /> {t("home.followingTab")}
        </button>
        <button
          style={activeTab === "explore" ? tabActive : tabBtn}
          onClick={() => setActiveTab("explore")}
          aria-pressed={activeTab === "explore"}
        >
          <IoCompass size={15} /> {t("home.exploreTab")}
        </button>
      </div>

      {!initialLoaded ? (
        <>
          {slowLoad && (
            <p style={slowLoadText}>
              {t("home.slowLoad")}{" "}
              <button style={slowLoadBtn} onClick={() => window.location.reload()}>
                {t("home.tryAgain")}
              </button>
            </p>
          )}
          <PostSkeleton />
        </>
      ) : (
        <div style={grid}>
          {activeList.length > 0 && activeList.map(design => renderCard(design))}

          {activeList.length === 0 && (
            <p style={{ color: "var(--text-muted)" }}>{t("home.noDesigns")}</p>
          )}
        </div>
      )}

      {feedFresh && sentinelActive && <div ref={sentinelRef} style={{ height: 1 }} />}
      {loadingMore && <p style={loadingMoreText}>Loading more…</p>}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {editToast && <Toast text={editToast} onClose={() => setEditToast("")} />}

      {tagModalId && (
        <TagInfoModal designId={tagModalId} onClose={() => setTagModalId(null)} />
      )}

      {reportModalId && (
        <ReportModal
          reporterId={user?.id}
          targetType="design"
          targetId={reportModalId}
          onClose={() => setReportModalId(null)}
        />
      )}
    </div>
  );
}

// Self-contained — its own fetch, own loading state — so an admin marking a
// design "featured" doesn't need any change to the main following/suggested
// feed logic above. Renders nothing until it actually has featured designs
// to show, so there's no empty section or layout jump for most visitors.
function FeaturedStrip({ excludeIds }) {
  const [featured, setFeatured] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    let query = supabase
      .from("designs")
      .select("id, title, image_url")
      .eq("featured", true)
      .order("created_at", { ascending: false })
      .limit(10);

    // Every other feed surface (main feed, Trending, HashtagPage, Search)
    // hides blocked/muted users' content in both directions — this strip
    // was the one place that didn't, so a featured design from someone
    // you'd blocked still showed up here regardless.
    if (excludeIds.length > 0) {
      query = query.not("user_id", "in", `(${excludeIds.join(",")})`);
    }

    query.then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.log("LOAD FEATURED ERROR:", error);
      }
      setFeatured(data || []);
      setLoaded(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeIds.join(",")]);

  if (!loaded || featured.length === 0) return null;

  return (
    <div style={featuredWrap}>
      <div style={featuredHeader}>Featured</div>
      <div style={featuredScroll}>
        {featured.map(d => (
          <Link key={d.id} to={`/d/${d.id}`} style={featuredItem}>
            <LazyImage src={d.image_url} alt={d.title || "Featured design"} style={featuredImg} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default Home;

/* ===== STYLES ===== */

const page = {
  padding: "12px 4px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const loadingMoreText = {
  textAlign: "center",
  fontSize: 13,
  color: "var(--text-muted)",
  padding: "14px 0"
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: "10px 18px",
  marginTop: "4px"
};

const tabRow = {
  display: "flex",
  gap: 2,
  width: "fit-content",
  maxWidth: "100%",
  padding: 3,
  margin: "0 8px 14px",
  borderRadius: 999,
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)"
};

const tabBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px",
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: "var(--text-secondary)",
  fontWeight: 700,
  fontSize: 13.5,
  whiteSpace: "nowrap"
};

const tabActive = {
  ...tabBtn,
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)"
};

const featuredWrap = { padding: "0 8px 16px" };

const featuredHeader = {
  fontSize: "16px",
  fontWeight: 600,
  color: "var(--text-heading)",
  margin: "0 0 10px"
};

const featuredScroll = {
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 4
};

const featuredItem = {
  flexShrink: 0,
  width: 110,
  height: 140,
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid var(--border-soft)"
};

const featuredImg = {
  width: "100%",
  height: "100%",
  objectFit: "cover"
};

const card = {
  background: "var(--bg-surface)",
  borderRadius: "14px",
  overflow: "hidden",
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)",
  transition: "0.25s"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 24,
  padding: "4px 14px"
};

const followBtn = {
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  border: "none",
  borderRadius: "16px",
  padding: "6px 14px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer"
};

const dotsBtn = {
  display: "flex",
  background: "none",
  border: "none",
  fontSize: "22px",
  cursor: "pointer",
  color: "var(--text-primary)",
  lineHeight: 1,
  padding: "4px 4px"
};

const slowLoadText = {
  textAlign: "center",
  fontSize: 13.5,
  color: "var(--text-muted)",
  margin: "14px 0 0"
};

const slowLoadBtn = {
  border: "none",
  background: "none",
  padding: 0,
  fontSize: 13.5,
  fontWeight: 700,
  color: "var(--accent-primary)",
  cursor: "pointer"
};

const image = {
  width: "100%",
  height: "auto",
  display: "block",
  background: "var(--border-soft)"
};

const actionRow = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "2px 14px 0"
};

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  background: "none",
  border: "none",
  borderRadius: "20px",
  height: 40,
  padding: "0 6px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  color: "var(--text-primary)"
};

const commentCountText = {
  fontWeight: "bold",
  fontSize: "13px"
};

const content = {
  padding: "2px 14px 4px"
};

const saveBtn = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  border: "none",
  padding: "8px",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "13px"
};

const cancelBtn = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  background: "none",
  color: "var(--text-secondary)",
  border: "1.5px solid var(--border-input)",
  padding: "8px",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "13px"
};
